/**
 * IX NOVA — Privacy-safe PostHog wrapper.
 *
 * Contract:
 *   - Every function is a no-op if PostHog failed to load.
 *   - Every call is wrapped in try/catch. Analytics can NEVER break the app.
 *   - Only user_id + platform/version metadata is ever sent. Emails, names,
 *     photos, tokens, DOB, body metrics, and any prop key that mentions
 *     `password|token|email|name|photo|image|dob|weight|height|card|cvv`
 *     is stripped before capture (belt-and-braces on top of PostHog's own
 *     sanitize_properties hook in index.html).
 *   - Query strings are stripped from any URL-shaped values.
 *
 * Public API:
 *   track(event, props)
 *   trackScreen(path)
 *   identifyUser(userId, props)
 *   resetIdentity()
 *   isReady()
 */

// ---- config that is safe to expose in the client bundle ---------------------
const APP_VERSION = "1.1.0";
const BUILD_NUMBER = 24;

// Every property key containing one of these substrings will be redacted.
const REDACT_KEY_SUBSTRINGS = [
  "password", "passwd", "token", "authorization", "auth_",
  "email", "e_mail",
  "name", "full_name", "first_name", "last_name",
  "photo", "image", "image_base64", "base64", "avatar",
  "card", "cvv", "cvc", "pan", "iban",
  "dob", "date_of_birth", "birthday",
  "weight", "height", "measurements", "waist", "chest",
  "phone", "mobile", "address",
];

// Prop keys we always allow through even though they may loosely match above.
const ALLOWED_KEYS = new Set([
  "event_name", "feature", "sub_action", "provider", "plan_id",
  "interval", "amount", "currency", "error_code", "source_screen",
  "step_name", "step_index", "steps_total", "duration_sec",
  "path", "previous_path", "platform", "app_version", "build_number",
  "is_new_session", "referrer",
]);

// ---- platform detection -----------------------------------------------------
function detectPlatform() {
  try {
    // Capacitor exposes a global when running as a native app
    const cap =
      (typeof window !== "undefined" && window.Capacitor) ||
      (typeof globalThis !== "undefined" && globalThis.Capacitor);
    if (cap && typeof cap.getPlatform === "function") {
      const p = cap.getPlatform();
      if (p === "ios") return "ios";
      if (p === "android") return "android";
    }
    // Fallback UA sniff — only used for coarse web/mobile-web split.
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    if (/iPhone|iPad|iPod/.test(ua)) return "mobile_web_ios";
    if (/Android/.test(ua)) return "mobile_web_android";
    return "web";
  } catch {
    return "web";
  }
}

const PLATFORM = detectPlatform();

// ---- helpers ---------------------------------------------------------------
function ph() {
  try {
    return (typeof window !== "undefined" && window.posthog) || null;
  } catch {
    return null;
  }
}

function stripQuery(v) {
  if (typeof v !== "string") return v;
  const qi = v.indexOf("?");
  return qi > -1 ? v.substring(0, qi) : v;
}

function safeProps(input) {
  if (!input || typeof input !== "object") return {};
  const out = {};
  for (const k of Object.keys(input)) {
    if (!ALLOWED_KEYS.has(k)) {
      const lk = k.toLowerCase();
      let redact = false;
      for (const bad of REDACT_KEY_SUBSTRINGS) {
        if (lk.indexOf(bad) !== -1) { redact = true; break; }
      }
      if (redact) continue; // drop the key entirely
    }
    let v = input[k];
    if (typeof v === "string") {
      // Strip query strings from anything that looks like a URL
      if (/^https?:\/\//i.test(v) || v.startsWith("/")) v = stripQuery(v);
      // Cap very long strings (base64 blobs sneak in sometimes)
      if (v.length > 500) v = v.substring(0, 500) + "…";
    }
    out[k] = v;
  }
  return out;
}

function baseProps() {
  return {
    platform: PLATFORM,
    app_version: APP_VERSION,
    build_number: BUILD_NUMBER,
  };
}

// ---- public API ------------------------------------------------------------
export function isReady() {
  const p = ph();
  return !!(p && typeof p.capture === "function");
}

export function track(event, props) {
  try {
    const p = ph();
    if (!p || typeof p.capture !== "function") return;
    const payload = { ...baseProps(), ...safeProps(props) };
    p.capture(event, payload);
  } catch { /* never break the app */ }
}

export function trackScreen(path, previousPath) {
  try {
    const cleaned = stripQuery(String(path || ""));
    const prev = previousPath ? stripQuery(String(previousPath)) : undefined;
    track("screen_viewed", { path: cleaned, previous_path: prev });
  } catch { /* ignore */ }
}

/**
 * Identify a user by their internal UUID. We deliberately pass ONLY
 * non-PII metadata to person properties. No email, no name.
 */
export function identifyUser(userId, extra) {
  try {
    if (!userId) return;
    const p = ph();
    if (!p || typeof p.identify !== "function") return;
    const personProps = {
      platform: PLATFORM,
      app_version: APP_VERSION,
      build_number: BUILD_NUMBER,
      ...(extra && typeof extra === "object"
        ? safeProps({
            role: extra.role,
            premium: extra.premium,
            signup_date: extra.signup_date,
          })
        : {}),
    };
    p.identify(String(userId), personProps);
  } catch { /* ignore */ }
}

export function resetIdentity() {
  try {
    const p = ph();
    if (p && typeof p.reset === "function") p.reset();
  } catch { /* ignore */ }
}

// ---- one-time boot event ---------------------------------------------------
try {
  // Fire once per page load. PostHog handles session_started internally;
  // this is our explicit "app_opened" so we can funnel from it.
  if (typeof window !== "undefined" && !window.__ixnova_app_opened_fired) {
    window.__ixnova_app_opened_fired = true;
    // Defer a tick so PostHog snippet has time to swap in the real SDK.
    setTimeout(() => {
      track("app_opened", { is_new_session: true });
    }, 0);
  }
} catch { /* ignore */ }
